<template>
  <div
    :class="
      $vuetify.breakpoint.mobile
        ? 'flex-column'
        : 'd-flex justify-space-between align-center'
    "
  >
    <div class="d-flex flex-wrap">
      <SelectorFilter
        class="me-6 mb-2"
        :label="typeProperties.name"
        :id="typeProperties.key"
        :options="typeProperties.options"
        :multiselect="typeProperties.multiselect"
        :value="typeProperties.value"
        @change="onTypeChange"
      />
      <SelectorFilter
        v-if="shouldShowSiteFilter"
        allowClear
        class="me-6 mb-2"
        data-testid="taskFilters-selectorFilter-site"
        :id="siteProperties.key"
        :label="siteProperties.name"
        :multiselect="siteProperties.multiselect"
        :options="siteProperties.options"
        :value="siteProperties.value"
        @change="onSiteChange"
      />
      <SelectorFilter
        v-if="isListView"
        class="me-6 mb-2"
        :label="statusProperties.name"
        :id="statusProperties.key"
        :options="statusProperties.options"
        :multiselect="statusProperties.multiselect"
        :value="statusProperties.value"
        @change="onStatusChange"
      />
      <AssigneesSelectorFilter
        v-if="shouldShowAssigneeFilter"
        class="me-6 mb-2 assignees-filter"
        :multiple="assigneeProperties.multiselect"
        :availableAccounts="assigneeProperties.options"
        :initialAssignees="assigneeProperties.initialOptions"
        :selectedAccountIds="assigneeProperties.value"
        :label="assigneeProperties.name"
        :isLoading="isLoadingAccounts"
        @change="onAssigneeChange({ value: $event })"
        @clear="onAssigneeChange({ value: $event })"
        @search="fetchAccounts"
      />
    </div>
    <div class="d-flex align-center justify-end">
      <Menu
        :closeOnContentClick="false"
        :options="sortOptions"
        :selectedOption="sortBy"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            text
            color="white"
            class="mx-0 px-0 me-4"
            data-tourid="tasks-filters-sort"
          >
            <v-icon color="blue-grey darken-1" left>mdi-sort</v-icon>
            <span class="blue-grey--text text--darken-1 caption">
              {{ $t('common.sort.sort') }}: {{ sortText }}
            </span>
            <v-icon color="blue-grey darken-1" size="25" right
              >mdi-chevron-down</v-icon
            >
          </v-btn>
        </template>
      </Menu>
      <Menu
        v-if="isListView"
        :closeOnContentClick="false"
        :options="groupOptions"
        :selectedOption="groupBy"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            text
            color="white"
            class="mx-0 px-0 ml-4 me-4"
            data-tourid="tasks-filters-group"
          >
            <v-icon color="blue-grey darken-1" left
              >mdi-view-agenda-outline</v-icon
            >
            <span class="blue-grey--text text--darken-1 caption">
              {{ $t('filters.group_by') }}: {{ groupText }}
            </span>
            <v-icon color="blue-grey darken-1" size="25" right
              >mdi-chevron-down</v-icon
            >
          </v-btn>
        </template>
      </Menu>
      <ViewSwitch @change="onViewTypeSelected($event)" />
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { compact, isArray, isEmpty } from 'lodash'

import access from '@/lib/access.js'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import getEntityId from '@/lib/getEntityId'

import AssigneesSelectorFilter from '@/components/AssigneesSelector/AssigneesSelectorFilter.vue'
import SelectorFilter from '@/components/Filters/SelectorFilter.vue'
import Menu from '@/components/UI/Menu.vue'
import ViewSwitch from './ViewSwitch.vue'

const SORT_OPTIONS = {
  dueDate: 'dueDate-asc',
  lastUpdated: 'updatedAt-desc',
  lastCreated: 'createdAt-desc',
  alphabetical: 'name-asc',
}

const GROUP_OPTIONS = {
  dueDate: 'dueDate',
  priority: 'priority',
  all: 'all',
}

const TYPE_OPTIONS = {
  assignedToMe: 'assignedToMe',
  createdByMe: 'createdByMe',
  all: 'all',
}

export default {
  name: 'TaskFilters',

  components: {
    AssigneesSelectorFilter,
    SelectorFilter,
    Menu,
    ViewSwitch,
  },

  mixins: [mixin, trackerMixin],

  data() {
    return {
      filters: {
        status:
          isArray(this.$route.query.status) || !this.$route.query.status
            ? this.$route.query.status
            : [this.$route.query.status],
        excludedAssignees:
          isArray(this.$route.query.excludedAssignees) ||
          !this.$route.query.excludedAssignees
            ? this.$route.query.excludedAssignees
            : [this.$route.query.excludedAssignees],
        assignees:
          isArray(this.$route.query.assignees) || !this.$route.query.assignees
            ? this.$route.query.assignees
            : [this.$route.query.assignees],
        createdBy: this.$route.query.createdBy,
        site:
          isArray(this.$route.query.site) || !this.$route.query.site
            ? this.$route.query.site
            : [this.$route.query.site],
        type: this.$route.query.type || TYPE_OPTIONS.assignedToMe,
      },
      groupBy: this.$route.query.group || GROUP_OPTIONS.all,
      sortBy: this.$route.query.sort || SORT_OPTIONS.dueDate,
      viewType:
        this.$route.query.viewType || this.$constants.TASKS_VIEW_OPTIONS.list,
      accounts: [],
      initialAccounts: [],
      isLoadingAccounts: false,
    }
  },

  created() {
    if (isEmpty(this.$route.query) || !this.$route.query.type)
      // validating when the initial route has no filters or type to show always correct content
      this.setInitialFilters()
    this.fetchAccounts()
    this.fetchInitialAccounts()
  },
  computed: {
    ...mapGetters([
      'account',
      'authorizedSiteDetails',
      'organizationId',
      'organizationSites',
    ]),

    isListView() {
      return this.viewType === this.$constants.TASKS_VIEW_OPTIONS.list
    },

    hasMultiSiteScope() {
      return (
        Boolean(this.account?.isSiteAdmin) &&
        this.authorizedSiteDetails.length > 1
      )
    },

    userIsAdmin() {
      return access.isLeadership(this.account)
    },
    statusProperties() {
      return {
        key: 'status',
        name: this.$t('filters.status'),
        multiselect: false,
        options: this.statusOptions,
        value: this.filters.status?.[0] ?? '',
      }
    },
    statusOptions() {
      return [
        {
          text: this.$t('tasks.incomplete_tasks'),
          value: this.$constants.tasksStatus.open,
        },
        {
          text: this.$t('tasks.completed_tasks'),
          value: this.$constants.tasksStatus.completed,
        },
        {
          text: this.$t('tasks.all'),
          value: '',
        },
      ]
    },
    typeProperties() {
      return {
        key: 'type',
        name: this.$t('filters.filter_by'),
        multiselect: false,
        options: this.typeOptions,
        value: this.filters.type,
      }
    },
    typeOptions() {
      return compact([
        {
          text: this.$t('tasks.my_tasks'),
          value: TYPE_OPTIONS.assignedToMe,
        },
        {
          text: this.$t('filters.created_by_me'),
          value: TYPE_OPTIONS.createdByMe,
        },
        this.userIsAdmin && {
          text: access.isOrganizationAdmin(this.account)
            ? this.$t('tasks.all')
            : this.$t('tasks.site_tasks'),
          value: 'all',
        },
      ])
    },
    assigneeProperties() {
      return {
        key: 'assignees',
        name: this.$t('common.assignee_one'),
        multiselect: false,
        options: this.accounts,
        value: this.filters?.assignees?.[0] ?? '',
        initialOptions: this.initialAccounts,
      }
    },
    siteProperties() {
      return {
        key: 'site',
        name: this.$t('filters.site_other'),
        multiselect: true,
        options: this.siteOptions,
        value: this.filters?.site || [],
      }
    },
    sortOptions() {
      return compact([
        {
          title: this.$t('filters.due_date'),
          id: SORT_OPTIONS.dueDate,
          action: () => this.onSortSelected(SORT_OPTIONS.dueDate),
        },
        {
          title: this.$t('common.sort.last_updated'),
          id: SORT_OPTIONS.lastUpdated,
          action: () => this.onSortSelected(SORT_OPTIONS.lastUpdated),
        },
        {
          title: this.$t('common.sort.last_created'),
          id: SORT_OPTIONS.lastCreated,
          action: () => this.onSortSelected(SORT_OPTIONS.lastCreated),
        },
        {
          title: this.$t('common.alphabetically'),
          id: SORT_OPTIONS.alphabetical,
          action: () => this.onSortSelected(SORT_OPTIONS.alphabetical),
        },
      ])
    },
    groupOptions() {
      return compact([
        {
          title: this.$t('filters.due_date'),
          id: GROUP_OPTIONS.dueDate,
          action: () => this.onGroupSelected(GROUP_OPTIONS.dueDate),
        },
        {
          title: this.$t('common.priority'),
          id: GROUP_OPTIONS.priority,
          action: () => this.onGroupSelected(GROUP_OPTIONS.priority),
        },
        {
          title: this.$t('tasks.all'),
          id: GROUP_OPTIONS.all,
          action: () => this.onGroupSelected(GROUP_OPTIONS.all),
        },
      ])
    },
    siteOptions() {
      const organizationSites = this.hasMultiSiteScope
        ? this.authorizedSiteDetails.map(site => ({
            ...site,
            id: getEntityId(site),
          }))
        : this.organizationSites

      return organizationSites.map(site => {
        return {
          ...site,
          text: site.name,
          value: site.id,
          subtitle: this.$t(`countries.${site.countryCodes[0]}`),
          subtitleIcon: 'mdi-earth',
        }
      })
    },
    sortText() {
      return this.sortOptions.find(option => this.sortBy === option.id)?.title
    },
    groupText() {
      return this.groupOptions.find(option => this.groupBy === option.id)?.title
    },
    viewTypeText() {
      return this.viewOptions.find(option => this.viewType === option.id)?.title
    },
    shouldShowAssigneeFilter() {
      return Boolean(
        this.filters.type === TYPE_OPTIONS.createdByMe ||
          this.filters.type === TYPE_OPTIONS.all
      )
    },
    shouldShowSiteFilter() {
      return Boolean(
        this.filters.type === TYPE_OPTIONS.all &&
          (access.isOrganizationAdmin(this.account) || this.hasMultiSiteScope)
      )
    },
  },

  methods: {
    onTypeChange({ value }) {
      if (value === TYPE_OPTIONS.assignedToMe) {
        delete this.filters.site
        delete this.filters.createdBy
        delete this.filters.excludedAssignees
        this.filters.assignees = [this.account.id]
      }
      if (value === TYPE_OPTIONS.createdByMe) {
        delete this.filters.site
        delete this.filters.assignees
        this.filters.createdBy = this.account.id
        this.filters.excludedAssignees = [this.account.id]
      }
      if (value === TYPE_OPTIONS.all) {
        delete this.filters.createdBy
        delete this.filters.excludedAssignees
        delete this.filters.assignees
        this.filters.site = [getEntityId(this.account.site)]
      }

      this.filters = { ...this.filters, type: value }
      this.setFiltersOnRoute()
    },

    onStatusChange({ value }) {
      if (value === this.$constants.tasksStatus.open) {
        this.filters.status = [
          this.$constants.tasksStatus.open,
          this.$constants.tasksStatus.inProgress,
        ]
      } else {
        this.filters.status = value ? [value] : ''
      }
      this.setFiltersOnRoute()
    },

    onAssigneeChange({ value }) {
      this.filters = { ...this.filters, assignees: [value?._id] }
      this.setFiltersOnRoute()
    },

    onSiteChange({ value }) {
      const resolvedValue = isEmpty(value)
        ? [getEntityId(this.account.site)]
        : value
      this.filters = { ...this.filters, site: resolvedValue }
      this.setFiltersOnRoute()
    },

    onSortSelected(option) {
      this.sortBy = option
      this.setFiltersOnRoute()
    },

    onGroupSelected(option) {
      this.groupBy = option
      this.setFiltersOnRoute()
      this.$emit('groupChange', this.groupBy)
    },

    onViewTypeSelected(option) {
      this.trackTasks('tasks.changeView', {
        view: option,
      })
      this.viewType = option
      this.setFiltersOnRoute()
    },

    setFiltersOnRoute() {
      this.replaceRoute({
        path: 'tasks',
        query: {
          ...this.filters,
          task: this.$route.query.task,
          group: this.groupBy,
          sort: this.sortBy,
          viewType: this.viewType,
          searchTerms: this.$route.query?.searchTerms ?? null,
        },
      })

      this.trackTasks('tasks.filter', {
        sort: this.sortBy,
        groupBy: this.groupBy,
        status: this.filters.status,
        type: this.filters.type,
        hasTerms: Boolean(this.$route.query.searchTerms),
        hasAssignee: Boolean(this.filters.assignees?._id),
      })
    },

    setInitialFilters() {
      this.filters = {
        type: TYPE_OPTIONS.assignedToMe,
        assignees: [this.account.id],
        status: [
          this.$constants.tasksStatus.open,
          this.$constants.tasksStatus.inProgress,
        ],
      }
      this.setFiltersOnRoute()
    },

    async fetchAccounts(terms = '') {
      try {
        this.isLoadingAccounts = true
        const filters = { isDeleted: false }
        if (!terms) filters.site = this.account?.site?._id
        const response = await api.account.getAccounts(this.organizationId, {
          filters,
          terms,
        })
        this.accounts = response?.data?.results
      } catch (err) {
        this.showError(err)
      } finally {
        this.isLoadingAccounts = false
      }
    },

    async fetchInitialAccounts() {
      if (!this.filters?.assignees?.length) return
      const response = await api.account.getAccountsByIds(this.organizationId, {
        filters: { id: this.filters?.assignees },
      })
      this.initialAccounts = response.data.results
    },
  },
}
</script>

<style lang="scss" scoped>
.assignees-filter {
  max-width: 200px;
}
</style>
